import { Badge } from '@daily/shared/components/Badge';
import { CaretUpIcon } from '@daily/shared/components/Icons/CaretUpIcon';
import { HomeIcon } from '@daily/shared/components/Icons/HomeIcon';
import { ScreenshareIcon } from '@daily/shared/components/Icons/ScreenshareIcon';
import { Menu, MenuButton } from '@daily/shared/components/Menu';
import { Stack, StackItem } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { ThemeScope } from '@daily/shared/contexts/Theme';
import {
  useParticipantIds,
  useParticipantProperty,
} from '@daily-co/daily-react';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { BreakoutCountdown } from '/components/Topbar/BreakoutCountdown';
import { useOrderedParticipantIds } from '/contexts/ParticipantsProvider';
import { useCallConfig } from '/hooks/useCallConfig';
import { useDisplayName } from '/hooks/useDisplayName';
import { useScreens } from '/hooks/useScreens';
import { useShowNames } from '/lib/state/custom';
import { useShowMobilePageMenu } from '/lib/state/layout';

import { PAGINATION_HEIGHT } from './constants';

interface Props {
  getParticipantsOnPage?(p: number): string[];
  onChangePage(p: number): void;
  page: number;
  pages: number;
}

export const MobilePaginationBar: React.FC<React.PropsWithChildren<Props>> = ({
  getParticipantsOnPage,
  onChangePage,
  page,
  pages,
}) => {
  const { t } = useTranslation();
  const { broadcast, broadcastRole, enableChat } = useCallConfig();
  const orderedIds = useOrderedParticipantIds();
  const ownerIds = useParticipantIds({ filter: 'owner' });
  const screens = useScreens();
  const [showMenu, setShowMenu] = useShowMobilePageMenu();
  const { breakoutStatus } = useBreakoutSessionState();

  /**
   * In OOB calls, owners should always see the full count, not just visible
   * participants. Attendees can see the full count if chat is enabled. Otherwise
   * we don't share participant info.
   *
   * In non-OOB calls, show the usual participant count.
   */
  const count =
    broadcastRole === 'owner' ||
    (broadcastRole === 'attendee' && enableChat) ||
    !broadcast
      ? orderedIds.length + 1
      : ownerIds.length;

  const firstScreenParticipantUserName = useParticipantProperty(
    screens?.[0]?.session_id,
    'user_name'
  );

  const peopleInCall = useMemo(
    () => (
      <div className="people">
        <Text color="muted" truncate>
          {breakoutStatus === 'ongoing'
            ? t('breakouts.peopleInRoom', { count })
            : count <= 1 && !broadcast
            ? null
            : t('people.withCount', { count })}
          {screens.length > 0 && (
            <>
              {' '}
              (
              {screens.length > 1
                ? t('screenShare.peopleWithCount', {
                    count: screens.length,
                  })
                : t('screenShare.personWithName', {
                    name: firstScreenParticipantUserName || t('people.someone'),
                  })}
              )
            </>
          )}
        </Text>
        {broadcastRole === 'owner' && (
          <Badge
            style={{ alignSelf: 'flex-start', marginLeft: 'auto' }}
            variant="success"
          >
            {t('broadcast.live')}
          </Badge>
        )}
        <style jsx>{`
          .people {
            display: flex;
            flex-direction: row;
            gap: 8px;
            overflow: hidden;
            max-width: 100%;
          }
        `}</style>
      </div>
    ),
    [
      breakoutStatus,
      broadcast,
      broadcastRole,
      count,
      firstScreenParticipantUserName,
      screens.length,
      t,
    ]
  );

  const handleClickMenuButton = () => {
    setShowMenu(true);
  };

  const handleClickPage = (p: number) => {
    onChangePage(p);
    setShowMenu(false);
  };

  const menuItems: React.ComponentProps<typeof Menu>['items'] = [
    {
      label: (
        <Stack align="start" gap={0} width="100%">
          <Text El="span" variant="large">
            {screens.length ? t('general.screenShare') : t('general.homePage')}
          </Text>
          <StackItem style={{ maxWidth: '100%' }}>
            <Text color="muted" truncate>
              {getParticipantsOnPage(1).map((id, idx) => (
                <Fragment key={id}>
                  {idx > 0 && ', '}
                  <ParticipantName key={id} sessionId={id} />
                </Fragment>
              ))}
            </Text>
          </StackItem>
        </Stack>
      ),
      icon: screens.length ? (
        <ScreenshareIcon size={24} />
      ) : (
        <HomeIcon size={24} />
      ),
      onClick: () => handleClickPage(1),
    },
  ];

  for (let i = 2; i <= pages; i++) {
    menuItems.push({
      label: (
        <Stack align="start" gap={0} width="100%">
          <Text El="span" variant="large">
            {t('general.page', { page: i })}
          </Text>
          <StackItem style={{ maxWidth: '100%' }}>
            <Text color="muted" truncate>
              {getParticipantsOnPage(i).map((id, idx) => (
                <Fragment key={id}>
                  {idx > 0 && ', '}
                  <ParticipantName key={id} sessionId={id} />
                </Fragment>
              ))}
            </Text>
          </StackItem>
        </Stack>
      ),
      onClick: () => handleClickPage(i),
    });
  }

  return (
    <ThemeScope theme="dark">
      <div className="bar">
        {count < 2 ? (
          <div className="waiting">
            <Text>{t('people.waitingForOthers')}</Text>
          </div>
        ) : (
          <MenuButton
            aria-controls="mobile-page-menu"
            className="page-menu-btn"
            id="mobile-page-menu-btn"
            onClick={handleClickMenuButton}
            open={showMenu}
            variant="ghost"
          >
            <div className="page">
              <Text>
                {page === 1
                  ? screens.length
                    ? t('general.screenShare')
                    : t('general.homePage')
                  : t('general.page', { page })}
              </Text>
              <CaretUpIcon size={16} />
            </div>
            {peopleInCall}
          </MenuButton>
        )}
        {showMenu && (
          <Menu
            aria-labelledby="mobile-page-menu-btn"
            id="mobile-page-menu"
            items={menuItems}
            onClose={() => setShowMenu(false)}
            placement="top"
            offsetLeft={-8}
            cardProps={{
              style: {
                maxHeight: '50vh',
                maxWidth: 360,
                overflowX: 'hidden',
                overflowY: 'auto',
                width: 'calc(100vw - 16px)',
              },
            }}
          />
        )}
        <BreakoutCountdown />
      </div>
      <style jsx>{`
        .bar {
          --status-gap: 2px;

          align-items: center;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          padding: calc(calc(${PAGINATION_HEIGHT}px - 32px) / 2) 16px;
          width: 100%;
        }
        .waiting {
          margin: 0 auto;
        }
        .bar :global(.page-menu-btn) {
          align-items: flex-start;
          display: flex;
          flex-direction: column;
          max-width: 100%;
        }
        .bar :global(.page-menu-btn) .page {
          align-items: center;
          display: flex;
          gap: 4px;
        }
      `}</style>
    </ThemeScope>
  );
};

// @ts-ignore
const ParticipantName: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const [showNames] = useShowNames();
  const name = useDisplayName(sessionId, showNames);
  return name;
};
